import { useEffect, useState } from 'preact/hooks';
import type { ChatMessage } from '@/agent/types';
import { validateAction } from '@/agent/proposals';
import { confirmProposal, dismissProposal } from '@/state/chat';
import { Icon } from '@/components/Icon';
import { navigate } from '@/state/nav';

/** Proposed-actions card (HANDOFF §preview-card + anims #7-#10).
 *  Single log_feeding proposals auto-confirm after 4s unless touched. */
export function PreviewCard({ message }: { message: ChatMessage }) {
  const proposal = message.proposal!;
  const validity = proposal.actions.map((a) => validateAction(a));
  const [checked, setChecked] = useState<boolean[]>(validity.map((v) => v.ok));
  const [countdown, setCountdown] = useState<number | null>(null);
  const pending = message.proposalStatus === 'pending';

  const autoConfirmable =
    pending && proposal.actions.length === 1 && proposal.actions[0].type === 'log_feeding' && validity[0].ok;

  useEffect(() => {
    if (!autoConfirmable) return;
    setCountdown(4);
    const iv = setInterval(() => {
      setCountdown((c) => {
        if (c === null) {
          clearInterval(iv);
          return null;
        }
        if (c <= 1) {
          clearInterval(iv);
          void confirmProposal(message.id, [0]);
          return null;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id]);

  // If the proposal leaves 'pending' by any path (Dismiss, apply elsewhere),
  // kill the auto-confirm countdown.
  useEffect(() => {
    if (!pending) setCountdown(null);
  }, [pending]);

  const cancelAuto = () => setCountdown(null);

  if (message.proposalStatus === 'applied') {
    return (
      <div class="pcard pcard--receipt">
        <div class="pcard__receipt">
          <Icon name="check" size={16} />
          <span>
            {message.appliedLabels?.length === 1
              ? message.appliedLabels[0]
              : `${message.appliedLabels?.length ?? 0} changes applied`}
          </span>
          {message.reportId && (
            <button class="pcard__link" onClick={() => navigate(`/reports/${message.reportId}`)}>
              Open report
            </button>
          )}
        </div>
      </div>
    );
  }

  if (message.proposalStatus === 'dismissed') {
    return (
      <div class="pcard pcard--receipt pcard--dismissed">
        <div class="pcard__receipt">
          <Icon name="x" size={14} />
          <span>Not applied</span>
        </div>
      </div>
    );
  }

  const toggle = (i: number) => {
    cancelAuto();
    if (!validity[i].ok) return;
    setChecked((c) => c.map((v, j) => (j === i ? !v : v)));
  };

  const anyChecked = checked.some((v, i) => v && validity[i].ok);

  return (
    <div class="pcard" onPointerDown={cancelAuto}>
      <div class="pcard__hd">{proposal.summary}</div>
      {proposal.actions.map((a, i) => (
        <button key={i} class="pcard__row" onClick={() => toggle(i)} disabled={!validity[i].ok}>
          <span class={`cb ${checked[i] && validity[i].ok ? '' : 'cb--off'} ${!validity[i].ok ? 'cb--invalid' : ''}`}>
            {validity[i].ok ? <Icon name="check" size={14} /> : <Icon name="x" size={12} />}
          </span>
          <div style={{ flex: 1, textAlign: 'left' }}>
            <div style={{ fontWeight: 600 }}>{a.label}</div>
            {(a.detail || !validity[i].ok) && (
              <div class="pcard__detail">
                {validity[i].ok ? a.detail : `Can't apply — ${validity[i].error}`}
              </div>
            )}
          </div>
        </button>
      ))}
      <div class="pcard__ft">
        <button class="btn btn--ghost btn--sm" onClick={() => { cancelAuto(); dismissProposal(message.id); }}>
          Dismiss
        </button>
        <button
          class="btn btn--sm"
          disabled={!anyChecked}
          onClick={() => {
            cancelAuto();
            void confirmProposal(
              message.id,
              checked.map((v, i) => (v ? i : -1)).filter((i) => i >= 0)
            );
          }}
        >
          {countdown !== null ? `Confirm · ${countdown}` : 'Confirm'}
        </button>
      </div>
    </div>
  );
}
